import { FundingPolicyType } from './appModel';
import { InsuranceCarrier } from './insuranceCarrierDefs';
import { toRecord } from './toRecord';

export interface FundingPolicyTypeDef {
  value: FundingPolicyType;
  key: keyof typeof FundingPolicyType;
  label: string;
  isJoint: boolean;
  description: string;
  calculationType: FundingPolicyType;
  genericType?: FundingPolicyType;
  isGeneric?: boolean;
  carrier?: string;
  carrierId?: InsuranceCarrier;
  isGroupPolicy?: boolean;
  /** Hidden from policy-type selection and document-upload inference */
  isDisabled?: boolean;
  /**
   * Calculation modeling has not been verified against a carrier illustration
   * and may contain modeling errors (e.g. unsupported product variants).
   * Surfaced to advisors as a heads-up alert in the Add/Edit Policy slide-out.
   */
  hasUnverifiedModeling?: boolean;
  extractionMethod?: 'file' | 'text';
  carrierKeywordMatches?: string[];
  productNameKeywordMatches?: string[];
}

const genericFundingPolicyTypeDefList: FundingPolicyTypeDef[] = [
  {
    value: FundingPolicyType.longTermCareInsurance,
    key: FundingPolicyType[FundingPolicyType.longTermCareInsurance] as FundingPolicyTypeDef['key'],
    label: 'Traditional LTC Policy',
    isJoint: false,
    description: 'Standalone long-term care insurance policy',
    calculationType: FundingPolicyType.longTermCareInsurance,
    isGeneric: true,
  },
  {
    value: FundingPolicyType.jointLongTermCareInsurance,
    key: FundingPolicyType[
      FundingPolicyType.jointLongTermCareInsurance
    ] as FundingPolicyTypeDef['key'],
    label: 'Joint Traditional LTC Policy',
    isJoint: true,
    description: 'Traditional LTC Policy with shared care benefits',
    calculationType: FundingPolicyType.longTermCareInsurance,
    isGeneric: true,
  },
  {
    value: FundingPolicyType.lifeInsuranceWithRider,
    key: FundingPolicyType[FundingPolicyType.lifeInsuranceWithRider] as FundingPolicyTypeDef['key'],
    label: 'Life with Benefits Rider',
    isJoint: false,
    description: 'Single death benefit pool that can be used for long-term care',
    calculationType: FundingPolicyType.lifeInsuranceWithRider,
    isGeneric: true,
  },
  {
    value: FundingPolicyType.hybridLifeInsurance,
    key: FundingPolicyType[FundingPolicyType.hybridLifeInsurance] as FundingPolicyTypeDef['key'],
    label: 'Hybrid Policy',
    isJoint: false,
    description: 'Death benefit and continuation of benefit amounts',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    isGeneric: true,
  },
  {
    value: FundingPolicyType.shortTermCareInsurance,
    key: FundingPolicyType[FundingPolicyType.shortTermCareInsurance] as FundingPolicyTypeDef['key'],
    label: 'Short Term Care Policy',
    isJoint: false,
    description: 'Maxmimum benefit with limited benefit period',
    calculationType: FundingPolicyType.shortTermCareInsurance,
    isGeneric: true,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.annuityHybrid,
    key: FundingPolicyType[FundingPolicyType.annuityHybrid] as FundingPolicyTypeDef['key'],
    label: 'Annuity Hybrid Policy',
    isJoint: false,
    description: 'Annuity value plus long-term care benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    isGeneric: true,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.jointHybridAssetBased,
    key: FundingPolicyType[FundingPolicyType.jointHybridAssetBased] as FundingPolicyTypeDef['key'],
    label: 'Joint Hybrid Policy',
    isJoint: true,
    description: 'Hybrid Policy with spousal benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    isGeneric: true,
  },
  {
    value: FundingPolicyType.jointHybridAnnuityBased,
    key: FundingPolicyType[
      FundingPolicyType.jointHybridAnnuityBased
    ] as FundingPolicyTypeDef['key'],
    label: 'Joint Annuity Hybrid Policy',
    isJoint: true,
    description: 'Annuity Hybrid with spousal benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    isGeneric: true,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.hybridLifeIra,
    key: FundingPolicyType[FundingPolicyType.hybridLifeIra] as FundingPolicyTypeDef['key'],
    label: 'Hybrid Life IRA',
    isJoint: false,
    description: 'Life insurance with LTC benefits funded by IRA',
    calculationType: FundingPolicyType.hybridLifeIra,
    isGeneric: true,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.jointHybridLifeIra,
    key: FundingPolicyType[FundingPolicyType.jointHybridLifeIra] as FundingPolicyTypeDef['key'],
    label: 'Joint Hybrid Life IRA',
    isJoint: true,
    description: 'Joint life with LTC benefits funded by IRA',
    calculationType: FundingPolicyType.hybridLifeIra,
    isGeneric: true,
    hasUnverifiedModeling: true,
  },
];

const carrierKeywordMatches: { [key: string]: string[] } = {
  oneAmerica: ['OneAmerica', 'One America'],
  mutualOfOmaha: ['Mutual of Omaha', 'MutualOfOmaha'],
  nationwide: ['Nationwide'],
  newYorkLife: ['New York Life', 'NYL'],
  securianFinancial: ['Securian Financial', 'Securian'],
  lincoln: ['Lincoln', 'Lincoln Financial'],
  globalAtlantic: ['Global Atlantic'],
  thrivent: ['Thrivent'],
  ngl: ['National Guardian Life'],
  transamerica: ['Transamerica'],
  careScout: ['CareScout'],
  equitrust: ['EquiTrust', 'Equitrust'],
  manhattanLife: ['ManhattanLife', 'Manhattan Life', 'Standard Life and Casualty'],
};

// Less than ideal but make sure that if one product name is a substring of another, the longer one comes after
// We'll always use to last match
const productNameKeywordMatches: { [key: string]: string[] } = {
  assetCare: ['AssetCare', 'Asset-Care', 'Asset Care'],
  assetcareThree: ['Asset Care III', 'Asset-Care III'],
  careMattersTwo: ['CareMatters II'],
  careMattersAnnuity: ['CareMatters Annuity'],
  financialFoundationIul: ['Financial Foundation IUL'],
  secureCare: ['SecureCare', 'Secure Care'],
  secureCareThree: ['SecureCare III'],
  secureCareFour: ['SecureCare IV'],
  assetFlex: ['Asset Flex'],
  moneyGuardFixedAdvantage: ['MoneyGuard Fixed Advantage'],
  foreCare: ['ForeCare'],
  annuityCareTwo: ['Annuity Care II'],
  noLapseGuaranteeLife: ['No-Lapse Guarantee UL II', 'No-Lapse Guarantee UL'],
  thriventLongTermCareInsurance: ['Long-term Care Insurance'],
  essentialLtc: ['EssentialLTC'],
  mutualCare: ['MutualCare'],
  careAssurance: ['Care Assurance', 'CareScout', 'CareScout Care Assurance'],
  indexedAnnuityCare: ['Indexed Annuity Care'],
  bridge: ['Bridge®', 'Bridge'],
  omniFlex: ['OmniFlex', 'Omni Flex', 'Manhattan Life Short Term Care'],
};

export const specificFundingPolicyTypeDefList: FundingPolicyTypeDef[] = [
  {
    value: FundingPolicyType.lifeWithIndexAccount,
    key: FundingPolicyType[FundingPolicyType.lifeWithIndexAccount] as FundingPolicyTypeDef['key'],
    label: 'Financial Foundation IUL',
    isJoint: false,
    description: 'Life insurance index account options',
    calculationType: FundingPolicyType.lifeInsuranceWithRider,
    carrier: 'Transamerica',
    carrierId: InsuranceCarrier.Transamerica,
    carrierKeywordMatches: carrierKeywordMatches.transamerica,
    productNameKeywordMatches: productNameKeywordMatches.financialFoundationIul,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.allstateHybridPolicy,
    key: FundingPolicyType[FundingPolicyType.allstateHybridPolicy] as FundingPolicyTypeDef['key'],
    label: 'Allstate Whole Life with LTC Insurance',
    isJoint: false,
    description: 'Life insurance with LTC benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'Allstate',
    carrierId: InsuranceCarrier.Allstate,
    isGroupPolicy: true,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.fortifiedLife,
    key: FundingPolicyType[FundingPolicyType.fortifiedLife] as FundingPolicyTypeDef['key'],
    label: 'Fortified Life',
    isJoint: false,
    description: 'Membership program that provides home-based services',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'Western Home',
    carrierId: InsuranceCarrier.WesternHome,
  },
  {
    value: FundingPolicyType.stayYourWay,
    key: FundingPolicyType[FundingPolicyType.stayYourWay] as FundingPolicyTypeDef['key'],
    label: 'Stay Your Way',
    isJoint: false,
    description: 'Membership program that provides home-based services',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'Stay Your Way',
    carrierId: InsuranceCarrier.StayYourWay,
  },
  {
    value: FundingPolicyType.nylSecureCare,
    key: FundingPolicyType[FundingPolicyType.nylSecureCare] as FundingPolicyTypeDef['key'],
    label: 'SecureCare',
    isJoint: false,
    description: 'SecureCare Long-Term Care Insurance',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'New York Life',
    carrierId: InsuranceCarrier.NewYorkLife,
    carrierKeywordMatches: carrierKeywordMatches.newYorkLife,
    productNameKeywordMatches: productNameKeywordMatches.secureCare,
  },
  {
    value: FundingPolicyType.nylAssetFlex,
    key: FundingPolicyType[FundingPolicyType.nylAssetFlex] as FundingPolicyTypeDef['key'],
    label: 'Asset Flex',
    isJoint: false,
    description: 'Fixed Premium UL with LTC Benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'New York Life',
    carrierId: InsuranceCarrier.NewYorkLife,
    carrierKeywordMatches: carrierKeywordMatches.newYorkLife,
    productNameKeywordMatches: productNameKeywordMatches.assetFlex,
  },
  {
    value: FundingPolicyType.nylJointSecureCare,
    key: FundingPolicyType[FundingPolicyType.nylJointSecureCare] as FundingPolicyTypeDef['key'],
    label: 'Joint SecureCare',
    isJoint: true,
    description: 'SecureCare with Shared Care Rider',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'New York Life',
    carrierId: InsuranceCarrier.NewYorkLife,
  },
  {
    value: FundingPolicyType.secureCareThree,
    key: FundingPolicyType[FundingPolicyType.secureCareThree] as FundingPolicyTypeDef['key'],
    label: 'SecureCare III',
    isJoint: false,
    description: 'Linked benefit product with cash indemnity LTC benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'Securian Financial',
    carrierId: InsuranceCarrier.SecurianFinancial,
    carrierKeywordMatches: carrierKeywordMatches.securianFinancial,
    productNameKeywordMatches: productNameKeywordMatches.secureCareThree,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.secureCareFour,
    key: FundingPolicyType[FundingPolicyType.secureCareFour] as FundingPolicyTypeDef['key'],
    label: 'SecureCare IV',
    isJoint: false,
    description: 'Linked benefit product with cash indemnity LTC benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'Securian Financial',
    carrierId: InsuranceCarrier.SecurianFinancial,
    carrierKeywordMatches: carrierKeywordMatches.securianFinancial,
    productNameKeywordMatches: productNameKeywordMatches.secureCareFour,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.moneyGuardFixedAdvantage,
    key: FundingPolicyType[
      FundingPolicyType.moneyGuardFixedAdvantage
    ] as FundingPolicyTypeDef['key'],
    label: 'MoneyGuard Fixed Advantage',
    isJoint: false,
    description: 'Universal life insurance with a long-term care rider',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'Lincoln Financial',
    carrierId: InsuranceCarrier.LincolnFinancial,
    carrierKeywordMatches: carrierKeywordMatches.lincoln,
    productNameKeywordMatches: productNameKeywordMatches.moneyGuardFixedAdvantage,
  },
  {
    value: FundingPolicyType.assetCare,
    key: FundingPolicyType[FundingPolicyType.assetCare] as FundingPolicyTypeDef['key'],
    label: 'Asset Care',
    isJoint: false,
    description: 'Asset-based LTC hybrid policy',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'OneAmerica',
    carrierId: InsuranceCarrier.OneAmerica,
    carrierKeywordMatches: carrierKeywordMatches.oneAmerica,
    productNameKeywordMatches: productNameKeywordMatches.assetCare,
  },
  {
    value: FundingPolicyType.careMattersTwo,
    key: FundingPolicyType[FundingPolicyType.careMattersTwo] as FundingPolicyTypeDef['key'],
    label: 'CareMatters II',
    isJoint: false,
    description: 'Cash indemnity LTC policy',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'Nationwide',
    carrierId: InsuranceCarrier.Nationwide,
    carrierKeywordMatches: carrierKeywordMatches.nationwide,
    productNameKeywordMatches: productNameKeywordMatches.careMattersTwo,
  },
  {
    value: FundingPolicyType.jointAssetCare,
    key: FundingPolicyType[FundingPolicyType.jointAssetCare] as FundingPolicyTypeDef['key'],
    label: 'Joint Asset Care',
    isJoint: true,
    description: 'Asset-based LTC hybrid policy with joint benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    genericType: FundingPolicyType.jointHybridAssetBased,
    carrier: 'OneAmerica',
    carrierId: InsuranceCarrier.OneAmerica,
  },
  {
    value: FundingPolicyType.jointCareMatters,
    key: FundingPolicyType[FundingPolicyType.jointCareMatters] as FundingPolicyTypeDef['key'],
    label: 'CareMatters Together',
    isJoint: true,
    description: 'Cash indemnity LTC policy with joint benefits',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    genericType: FundingPolicyType.jointHybridAssetBased,
    carrier: 'Nationwide',
    carrierId: InsuranceCarrier.Nationwide,
  },
  // Nationwide CareMatters Annuity. Despite the shared CareMatters name this is
  // an ANNUITY chassis, not the life chassis CareMatters II uses: one single
  // premium buys a contract value, the LTC pool is a multiple of that value, and
  // the death benefit is whatever contract value LTC has not consumed. Hence
  // `annuityHybrid` rather than `hybridLifeInsurance`, and hence
  // `isCareMattersFamily` deliberately does NOT cover these two — routing them
  // to cmTwoStrategy would apply a flat base pool plus a separate inflation
  // rider stream, which is not how this product pays.
  //
  // Modeling reference, with page citations to real illustrations:
  // calculations/docs/nationwide-carematters-annuity/README.md
  {
    value: FundingPolicyType.careMattersAnnuity,
    key: FundingPolicyType[FundingPolicyType.careMattersAnnuity] as FundingPolicyTypeDef['key'],
    label: 'CareMatters Annuity',
    isJoint: false,
    description: 'Cash indemnity LTC annuity',
    calculationType: FundingPolicyType.annuityHybrid,
    carrier: 'Nationwide',
    carrierId: InsuranceCarrier.Nationwide,
    carrierKeywordMatches: carrierKeywordMatches.nationwide,
    productNameKeywordMatches: productNameKeywordMatches.careMattersAnnuity,
  },
  {
    value: FundingPolicyType.jointCareMattersAnnuity,
    key: FundingPolicyType[
      FundingPolicyType.jointCareMattersAnnuity
    ] as FundingPolicyTypeDef['key'],
    label: 'CareMatters Annuity (Joint)',
    isJoint: true,
    description: 'Cash indemnity LTC annuity with joint benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    genericType: FundingPolicyType.jointHybridAnnuityBased,
    carrier: 'Nationwide',
    carrierId: InsuranceCarrier.Nationwide,
  },
  {
    value: FundingPolicyType.forecareFixedAnnuity,
    key: FundingPolicyType[FundingPolicyType.forecareFixedAnnuity] as FundingPolicyTypeDef['key'],
    label: 'ForeCare',
    isJoint: false,
    description: 'Fixed annuity with LTC benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    carrier: 'Global Atlantic',
    carrierId: InsuranceCarrier.GlobalAtlantic,
    extractionMethod: 'file',
    carrierKeywordMatches: carrierKeywordMatches.globalAtlantic,
    productNameKeywordMatches: productNameKeywordMatches.foreCare,
  },
  {
    value: FundingPolicyType.annuityCareTwo,
    key: FundingPolicyType[FundingPolicyType.annuityCareTwo] as FundingPolicyTypeDef['key'],
    label: 'AnnuityCare II',
    isJoint: false,
    description: 'Deferred annuity with LTC benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    carrier: 'OneAmerica',
    carrierId: InsuranceCarrier.OneAmerica,
    carrierKeywordMatches: carrierKeywordMatches.oneAmerica,
    productNameKeywordMatches: productNameKeywordMatches.annuityCareTwo,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.jointForecareFixedAnnuity,
    key: FundingPolicyType[
      FundingPolicyType.jointForecareFixedAnnuity
    ] as FundingPolicyTypeDef['key'],
    label: 'Joint ForeCare',
    isJoint: true,
    description: 'Fixed annuity with LTC benefits with joint benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    genericType: FundingPolicyType.jointHybridAnnuityBased,
    carrier: 'Global Atlantic',
    carrierId: InsuranceCarrier.GlobalAtlantic,
    extractionMethod: 'file',
  },
  {
    value: FundingPolicyType.assetcareThree,
    key: FundingPolicyType[FundingPolicyType.assetcareThree] as FundingPolicyTypeDef['key'],
    label: 'AssetCare III',
    isJoint: false,
    description: 'Deferred IRA and Whole Life with LTC benefits',
    calculationType: FundingPolicyType.hybridLifeIra,
    carrier: 'OneAmerica',
    carrierId: InsuranceCarrier.OneAmerica,
    carrierKeywordMatches: carrierKeywordMatches.oneAmerica,
    productNameKeywordMatches: productNameKeywordMatches.assetcareThree,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.jointAssetcareThree,
    key: FundingPolicyType[FundingPolicyType.jointAssetcareThree] as FundingPolicyTypeDef['key'],
    label: 'Joint AssetCare III',
    isJoint: true,
    description: 'Deferred IRA and Whole Life with LTC benefits for two',
    calculationType: FundingPolicyType.hybridLifeIra,
    genericType: FundingPolicyType.jointHybridLifeIra,
    carrier: 'OneAmerica',
    carrierId: InsuranceCarrier.OneAmerica,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.noLapseGuaranteeLife,
    key: FundingPolicyType[FundingPolicyType.noLapseGuaranteeLife] as FundingPolicyTypeDef['key'],
    label: 'No-Lapse Guarantee UL II',
    isJoint: false,
    description: 'UL with LTC benefits',
    calculationType: FundingPolicyType.lifeInsuranceWithRider,
    carrier: 'Nationwide',
    carrierId: InsuranceCarrier.Nationwide,
    carrierKeywordMatches: carrierKeywordMatches.nationwide,
    productNameKeywordMatches: productNameKeywordMatches.noLapseGuaranteeLife,
  },
  {
    value: FundingPolicyType.thriventLongTermCareInsurance,
    key: FundingPolicyType[
      FundingPolicyType.thriventLongTermCareInsurance
    ] as FundingPolicyTypeDef['key'],
    label: 'Long-term Care Insurance',
    isJoint: false,
    description: 'Long-term care insurance',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'Thrivent',
    carrierId: InsuranceCarrier.Thrivent,
    carrierKeywordMatches: carrierKeywordMatches.thrivent,
    productNameKeywordMatches: productNameKeywordMatches.thriventLongTermCareInsurance,
  },
  {
    value: FundingPolicyType.jointThriventLongTermCareInsurance,
    key: FundingPolicyType[
      FundingPolicyType.jointThriventLongTermCareInsurance
    ] as FundingPolicyTypeDef['key'],
    label: 'Long-term Care Insurance with Shared Care',
    isJoint: true,
    description: 'Long-term care insurance with shared benefits',
    calculationType: FundingPolicyType.longTermCareInsurance,
    genericType: FundingPolicyType.jointLongTermCareInsurance,
    carrier: 'Thrivent',
    carrierId: InsuranceCarrier.Thrivent,
  },
  {
    value: FundingPolicyType.nglEssentialLtc,
    key: FundingPolicyType[FundingPolicyType.nglEssentialLtc] as FundingPolicyTypeDef['key'],
    label: 'EssentialLTC',
    isJoint: false,
    description: 'Long-term care insurance',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'NGL',
    carrierId: InsuranceCarrier.NGL,
    carrierKeywordMatches: carrierKeywordMatches.ngl,
    productNameKeywordMatches: productNameKeywordMatches.essentialLtc,
  },
  {
    value: FundingPolicyType.jointNglEssentialLtc,
    key: FundingPolicyType[FundingPolicyType.jointNglEssentialLtc] as FundingPolicyTypeDef['key'],
    label: 'EssentialLTC with Shared Benefits',
    isJoint: true,
    description: 'Long-term care insurance with shared benefits',
    calculationType: FundingPolicyType.longTermCareInsurance,
    genericType: FundingPolicyType.jointLongTermCareInsurance,
    carrier: 'NGL',
    carrierId: InsuranceCarrier.NGL,
  },
  {
    value: FundingPolicyType.mutualOfOmahaMutualcare,
    key: FundingPolicyType[
      FundingPolicyType.mutualOfOmahaMutualcare
    ] as FundingPolicyTypeDef['key'],
    label: 'MutualCare',
    isJoint: false,
    description: 'Long-term care insurance',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'Mutual of Omaha',
    carrierId: InsuranceCarrier.MutualOfOmaha,
    carrierKeywordMatches: carrierKeywordMatches.mutualOfOmaha,
    productNameKeywordMatches: productNameKeywordMatches.mutualCare,
  },
  {
    value: FundingPolicyType.jointMutualOfOmahaMutualcare,
    key: FundingPolicyType[
      FundingPolicyType.jointMutualOfOmahaMutualcare
    ] as FundingPolicyTypeDef['key'],
    label: 'MutualCare with Shared Care',
    isJoint: true,
    description: 'Long-term care insurance with shared benefits',
    calculationType: FundingPolicyType.longTermCareInsurance,
    genericType: FundingPolicyType.jointLongTermCareInsurance,
    carrier: 'Mutual of Omaha',
    carrierId: InsuranceCarrier.MutualOfOmaha,
  },
  {
    value: FundingPolicyType.agisGroupPolicy,
    key: FundingPolicyType[FundingPolicyType.agisGroupPolicy] as FundingPolicyTypeDef['key'],
    label: 'Trustmark Life + Care Insurance',
    isJoint: false,
    description: 'Long-term care insurance',
    calculationType: FundingPolicyType.hybridLifeInsurance,
    carrier: 'Trustmark',
    carrierId: InsuranceCarrier.Trustmark,
    isGroupPolicy: true,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.careScoutCareAssurance,
    key: FundingPolicyType[FundingPolicyType.careScoutCareAssurance] as FundingPolicyTypeDef['key'],
    label: 'Care Assurance',
    isJoint: false,
    description: 'Traditional long-term care insurance',
    calculationType: FundingPolicyType.longTermCareInsurance,
    carrier: 'CareScout',
    carrierId: InsuranceCarrier.CareScout,
    carrierKeywordMatches: carrierKeywordMatches.careScout,
    productNameKeywordMatches: productNameKeywordMatches.careAssurance,
  },
  {
    value: FundingPolicyType.indexedAnnuityCare,
    key: FundingPolicyType[FundingPolicyType.indexedAnnuityCare] as FundingPolicyTypeDef['key'],
    label: 'Indexed Annuity Care',
    isJoint: false,
    description: 'Fixed indexed annuity with LTC rider',
    calculationType: FundingPolicyType.hybridLifeIra,
    carrier: 'OneAmerica',
    carrierId: InsuranceCarrier.OneAmerica,
    carrierKeywordMatches: carrierKeywordMatches.oneAmerica,
    productNameKeywordMatches: productNameKeywordMatches.indexedAnnuityCare,
    hasUnverifiedModeling: true,
  },
  {
    value: FundingPolicyType.equitrustBridge,
    key: FundingPolicyType[FundingPolicyType.equitrustBridge] as FundingPolicyTypeDef['key'],
    label: 'Bridge®',
    isJoint: false,
    description: 'Fixed indexed annuity with LTC benefits',
    calculationType: FundingPolicyType.annuityHybrid,
    carrier: 'EquiTrust',
    carrierId: InsuranceCarrier.EquiTrust,
    carrierKeywordMatches: carrierKeywordMatches.equitrust,
    productNameKeywordMatches: productNameKeywordMatches.bridge,
    isDisabled: true,
  },
  {
    value: FundingPolicyType.manhattanLifeOmniFlexStc,
    key: FundingPolicyType[
      FundingPolicyType.manhattanLifeOmniFlexStc
    ] as FundingPolicyTypeDef['key'],
    label: 'ManhattanLife OmniFlex Short-Term Care',
    isJoint: false,
    description: 'Day-denominated short-term facility care with optional home health care rider',
    calculationType: FundingPolicyType.manhattanLifeOmniFlexStc,
    carrier: 'ManhattanLife',
    carrierId: InsuranceCarrier.ManhattanLife,
    carrierKeywordMatches: carrierKeywordMatches.manhattanLife,
    productNameKeywordMatches: productNameKeywordMatches.omniFlex,
    hasUnverifiedModeling: true,
  },
];

export const fundingPolicyTypeDefList: FundingPolicyTypeDef[] = [
  ...genericFundingPolicyTypeDefList,
  ...specificFundingPolicyTypeDefList,
];

export const fundingPolicyTypeDefs = Object.assign(
  toRecord(fundingPolicyTypeDefList, policyType => policyType.value),
  toRecord(fundingPolicyTypeDefList, policyType => policyType.key),
  toRecord(fundingPolicyTypeDefList, policyType => policyType.label),
);

export const individualToJointPolicyMap: Partial<Record<FundingPolicyType, FundingPolicyType>> = {
  [FundingPolicyType.longTermCareInsurance]: FundingPolicyType.jointLongTermCareInsurance,
  [FundingPolicyType.hybridLifeInsurance]: FundingPolicyType.jointHybridAssetBased,
  [FundingPolicyType.annuityHybrid]: FundingPolicyType.jointHybridAnnuityBased,
  [FundingPolicyType.nylSecureCare]: FundingPolicyType.nylJointSecureCare,
  [FundingPolicyType.assetCare]: FundingPolicyType.jointAssetCare,
  [FundingPolicyType.careMattersTwo]: FundingPolicyType.jointCareMatters,
  [FundingPolicyType.careMattersAnnuity]: FundingPolicyType.jointCareMattersAnnuity,
  [FundingPolicyType.forecareFixedAnnuity]: FundingPolicyType.jointForecareFixedAnnuity,
  [FundingPolicyType.assetcareThree]: FundingPolicyType.jointAssetcareThree,
  [FundingPolicyType.thriventLongTermCareInsurance]:
    FundingPolicyType.jointThriventLongTermCareInsurance,
  [FundingPolicyType.nglEssentialLtc]: FundingPolicyType.jointNglEssentialLtc,
  [FundingPolicyType.mutualOfOmahaMutualcare]: FundingPolicyType.jointMutualOfOmahaMutualcare,
} as const;

export const jointPolicyTypeToIndividualPolicyTypeMap: Partial<
  Record<FundingPolicyType, FundingPolicyType>
> = Object.fromEntries(
  Object.entries(individualToJointPolicyMap).map(([key, value]) => [value, key]),
);

// Policies whose death benefit is flat from day 1 — inflation riders apply to
// the LTC pool only, not to the payout to beneficiaries. This list is the
// exception; the standard hybrid behavior in the industry is flat DB.
//
// The generic hybrid types (`hybridLifeInsurance`, `jointHybridAssetBased`)
// are included BY DESIGN (P2-16/17): static-DB is the declared model for the
// generic hybrid strategy — `policyInflationProtectionOnFaceAmount` is
// deliberately not honored for DB growth there, diverging from the legacy
// calc which inflated the face via that rate. See the model header in
// `calculations/funding/projection/strategies/genericHybridStrategy.ts`.
//
// TODO(approach B): invert this — make flat DB the default for all hybrid /
// life-with-rider / hybrid-life-IRA policies, and replace this with an
// opt-in list of products that genuinely inflate the death benefit (if any).
// Requires verifying the remaining products (Asset Flex already flat per
// this list; still need illustrations for AssetCare III, Joint AssetCare III,
// Indexed Annuity Care, Trustmark, Transamerica FFIUL, Nationwide NLG UL II,
// and the generic upload fallbacks) before flipping the default.
export function hasFlatDeathBenefit(policyType: FundingPolicyType | null): boolean {
  if (!policyType) return false;
  return (
    policyType === FundingPolicyType.careMattersTwo ||
    policyType === FundingPolicyType.jointCareMatters ||
    policyType === FundingPolicyType.allstateHybridPolicy ||
    policyType === FundingPolicyType.secureCareThree ||
    policyType === FundingPolicyType.secureCareFour ||
    policyType === FundingPolicyType.moneyGuardFixedAdvantage ||
    policyType === FundingPolicyType.assetCare ||
    policyType === FundingPolicyType.jointAssetCare ||
    policyType === FundingPolicyType.nylAssetFlex ||
    policyType === FundingPolicyType.hybridLifeInsurance ||
    policyType === FundingPolicyType.jointHybridAssetBased
    // NOT listed: agisGroupPolicy (Trustmark) also routes to
    // genericHybridStrategy (engine-flat DB), but the P2-16/17 decision named
    // only the two generic types above — adding Trustmark would flip its
    // legacy-calc + client DB display and needs its own decision. Until then,
    // its legacy/display surfaces still inflate the face while the engine
    // holds it flat.
  );
}

export function getPolicyName(
  policy: Pick<PolicyFundingSource, 'policyCustomName' | 'policyType'> | null | undefined,
  fallback: string = 'Insurance',
): string {
  if (!policy) return fallback;
  const customName = policy.policyCustomName;
  if (customName !== null && customName !== undefined) {
    const trimmed = String(customName).trim();
    if (trimmed) return trimmed;
  }
  return policy.policyType ? fundingPolicyTypeDefs[policy.policyType].label : fallback;
}

export function getPolicyNameParts(
  policy: Pick<PolicyFundingSource, 'policyCustomName' | 'policyType'> | null | undefined,
  fallback: string = 'Insurance',
): { primary: string; secondary?: string } {
  if (!policy) return { primary: fallback };
  const typeLabel = policy.policyType ? fundingPolicyTypeDefs[policy.policyType].label : fallback;
  const customName = policy.policyCustomName;
  if (customName !== null && customName !== undefined) {
    const trimmed = String(customName).trim();
    if (trimmed) return { primary: trimmed, secondary: typeLabel };
  }
  return { primary: typeLabel };
}

// CCAH-style membership programs (Continuing Care at Home). They share
// distinct mechanics vs. traditional LTC/hybrid products: no ADL trigger,
// no waiting period, premiums continue through care, and the premium
// investment period extends to the end of LTC rather than the start.
export function isMembershipProgram(policyType: FundingPolicyType | null | undefined): boolean {
  if (!policyType) return false;
  return (
    policyType === FundingPolicyType.fortifiedLife || policyType === FundingPolicyType.stayYourWay
  );
}

// Products whose elimination period is satisfied by receiving care through
// the waiting period, then paid back retroactively as a lump sum.
export function defaultsRetroactiveWaitingPeriodReimbursement(
  policyType: FundingPolicyType | null | undefined,
): boolean {
  if (!policyType) return false;
  return (
    policyType === FundingPolicyType.careMattersTwo ||
    policyType === FundingPolicyType.jointCareMatters
  );
}

// Products where `policyBenefitUtilizedToDate` is deducted from the base benefit
// amount dollar-for-dollar while the inflation rider pays from a SEPARATE stream
// that never depletes the pool — so a figure including rider payouts
// over-deducts. On a mature claim the rider can be the majority of each payment.
//
// CareMatters only. That flat-pool-plus-separate-rider structure is what makes
// including rider dollars provably wrong, and it is unique to this family among
// the products we model; see
// calculations/docs/benefits-utilized-inflation-rider.md.
//
// The field is manually entered — nothing extracts it — so this is advisor
// guidance, not a calculation change.
//
// KNOWN GAP — New York. NY CareMatters II folds inflation into the acceleration
// rider, so on a 2-year NY policy the inflation dollars DO deplete the pool and
// the caveat is exactly backwards: there, the total the carrier paid IS the
// right figure to enter. This predicate takes only a policy type and NY is the
// same type as every other state, so it cannot tell them apart; correcting it
// needs the state threaded into the insurance form. See
// calculations/docs/carematters-ii-new-york/README.md.
//
// Deliberately NOT covered, despite also deducting utilization from a base
// benefit: nylAssetFlex, moneyGuardFixedAdvantage, assetCare and the Securian
// SecureCare products all grow the pool itself with inflation rather than paying
// a rider beside it, so the dollar-for-dollar deduction is consistent there.
// Currently coincides with `isCareMattersFamily`; kept as its own predicate so
// the call site states the reason and any scope change has one home.
export function hasBenefitsUtilizedInflationCaveat(
  policyType: FundingPolicyType | null | undefined,
): boolean {
  return isCareMattersFamily(policyType);
}

// Nationwide CareMatters II / CareMatters Together. These products use a flat
// base monthly benefit pool plus an inflation rider that pays a separate
// per-policy-year monthly stream — not the COB-pool model used by other
// hybrid life products. Routed to a dedicated calc path; calculationType is
// left as hybridLifeInsurance so the rest of the codebase still treats them
// as hybrids for UI / form / comparison purposes.
export function isCareMattersFamily(policyType: FundingPolicyType | null | undefined): boolean {
  if (!policyType) return false;
  return (
    policyType === FundingPolicyType.careMattersTwo ||
    policyType === FundingPolicyType.jointCareMatters
  );
}

// Nationwide CareMatters Annuity, single life and joint. Shares the CareMatters
// name with CareMatters II but not the chassis: an annuity whose LTC pool is a
// multiple of a compounding contract value, rather than life insurance with a
// flat base pool and a separate rider stream. Deliberately NOT folded into
// `isCareMattersFamily` — that predicate selects cmTwoStrategy, which would
// model this product wrongly.
export function isCareMattersAnnuity(policyType: FundingPolicyType | null | undefined): boolean {
  if (!policyType) return false;
  return (
    policyType === FundingPolicyType.careMattersAnnuity ||
    policyType === FundingPolicyType.jointCareMattersAnnuity
  );
}

// Whether the advisor's "Waiting Period Reimbursement" election means anything
// for this product.
//
// `hasRetroactiveWaitingPeriodReimbursement` is a stored, user-editable column,
// and cmTwoStrategy genuinely gates on it. Two products deliberately ignore it
// because the retroactive lump is a FIXED CONTRACTUAL FEATURE there, not a
// choice the buyer makes:
//
//   SecureCare III / IV     — IV p.7: the day-91 payment includes the three
//                             elimination-period months. secureCareStrategy
//                             documents the column as intentionally ignored.
//   CareMatters Annuity     — ICC25-NWLA-686.1 p.3: the elimination period is
//                             satisfied retroactively; there is no variant of
//                             this contract that omits it.
//
// The form used to offer the toggle for all three regardless, so an advisor
// could set "Not reimbursed" on a SecureCare or annuity policy and watch nothing
// change. Gating the control on this predicate keeps the form honest without a
// data backfill, and keeps one list rather than the form and the strategies each
// carrying their own idea of which products are elective.
export function honorsRetroactiveWaitingPeriodElection(
  policyType: FundingPolicyType | null | undefined,
): boolean {
  if (!policyType) return true;
  return !(
    isCareMattersAnnuity(policyType) ||
    policyType === FundingPolicyType.secureCareThree ||
    policyType === FundingPolicyType.secureCareFour
  );
}

// Months in the LTC Acceleration layer — the stretch paid out of the contract
// value itself, before the Extension of Benefits layer takes over. Constant
// across risk classes; only the extension lengthens. Illustration p.5, see
// calculations/docs/nationwide-carematters-annuity/README.md.
export const CARE_MATTERS_ANNUITY_ACCELERATION_MONTHS = { single: 24, joint: 36 } as const;

// The acceleration window for a quote-added policy, leaving every other product
// on whatever the caller already derived.
export function careMattersAnnuityAccelerationMonths(
  policyType: FundingPolicyType | null | undefined,
  fallback: number | null,
): number | null {
  if (!isCareMattersAnnuity(policyType)) return fallback;
  return policyType === FundingPolicyType.jointCareMattersAnnuity
    ? CARE_MATTERS_ANNUITY_ACCELERATION_MONTHS.joint
    : CARE_MATTERS_ANNUITY_ACCELERATION_MONTHS.single;
}

// Products whose LEGACY approximation is unacceptable — the claim-projection
// engine must price them, or we must decline to report a number at all.
//
// The engine prices every policy `pickStrategy` can route, so for most products
// legacy is only a fallback for the cases where no projection materialised, and
// a reasonable one. For these it is not: it would silently report a materially
// wrong figure that looks perfectly ordinary on a quote card. Callers must fail
// closed rather than fall through.
//
// CareMatters Annuity: the legacy annuity path models ONE dollar pool from
// `policyMaximumBenefitAmount`, which the ROI mapper fills with the contract
// value — 1x. The product pays 2x (Standard) or 3x (Preferred) across an
// acceleration layer plus an Extension of Benefits layer, so the legacy number
// understates the benefit by half or two thirds.
export function requiresClaimProjectionEngine(
  policyType: FundingPolicyType | null | undefined,
): boolean {
  return isCareMattersAnnuity(policyType);
}

// Products whose modeled numbers are known to be materially wrong, because the
// calc path they fall back to does not match the product's real mechanics. They
// have no projection strategy, so they land on legacy — and legacy is not a
// reasonable approximation for them:
//
// AnnuityCare II: a genuine second COB pool on top of the LTC Accumulated Value
// (`Total LTC Balance = LTCAV + COB`). The single-pool `annuityHybrid` legacy
// path models only one, understating the pool by 3-4x. See open-issues #17/#18.
//
// EquiTrust Bridge: the LTC pool is `Premium x Coverage Ratio` grown 3% compound
// and capped at 20 years, paid from a Benefit Base that never depletes the
// Accumulation Value — not ForeCare's "extract Total LTC Benefit at event age".
// Years 1-4 also vest at 20/40/60/80%, which is unmodeled. See open-issues
// #15/#16/#21.
//
// Distinct from `hasUnverifiedModeling`, which means "we have no carrier
// illustration to check our model against" and only surfaces a disclaimer. This
// predicate means "the number on screen is wrong", so the projection-details
// view is withheld entirely rather than annotated.
export function hasKnownIncorrectModeling(
  policyType: FundingPolicyType | null | undefined,
): boolean {
  return (
    policyType === FundingPolicyType.annuityCareTwo ||
    policyType === FundingPolicyType.equitrustBridge
  );
}

export function checkPolicyCalculationTypeEquals(
  policyType: FundingPolicyType | null,
  equalsPolicyType: FundingPolicyType | FundingPolicyType[],
): boolean {
  if (!policyType) {
    return false;
  }

  const policyCalculationType = fundingPolicyTypeDefs[policyType].calculationType;

  if (Array.isArray(equalsPolicyType)) {
    return equalsPolicyType.includes(policyCalculationType);
  }

  return policyCalculationType === equalsPolicyType;
}
